import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Layout } from 'antd'

import { Wizard, WizardStatus, WizardStep, StepStatus as WizardStepStatus } from 'core/domain/wizard/wizard'

import { Button, Modal, Notification, Skeleton, Tooltip } from 'common/components'
import { useBreakPoints, useQueryRequestV2 } from 'common/hooks'

import CancelDisclaimerModal from './CancelDisclaimerModal'
import OnNavigationDisclaimerModal from './OnNavigationDisclaimerModal'
import SidebarNavigation from './SidebarNavigation'
import { NavItemProps } from './models'

import './ProductClassModalStyles.less'

const { Content, Sider, Header, Footer } = Layout

declare type BackButtonType = 'cancel' | 'back'
declare type NextButtonType = 'next' | 'save' | 'finish'

interface ProductClassWizardModalProps {
  id?: string
  planPeriodId: string
  visible: boolean
  afterClose: () => void
  onSubmit?: () => Promise<any>
  editingStep?: string
  getProductClassWizard: (planPeriodId: string, productId?: string) => Promise<Wizard>
  getProductClassWizardState: (planPeriodId: string, productId?: string) => Promise<any>
  navigationMapping: NavItemProps[]
  finishWizardMessage?: string
  wizardTitle?: string
}

interface ProductClassStatus {
  editing: boolean
  title: string | null
}

const ProductClassWizardModal = (props: ProductClassWizardModalProps) => {
  const { t: translate } = useTranslation()
  const [navigationItems, setNavigationItems] = useState<WizardStep[]>([])
  const [productClassStatus, setProductClassStatus] = useState<ProductClassStatus | undefined>(undefined)
  const [productClassId, setProductClassId] = useState<string | undefined>(props.id)
  const [currentNavigationItem, setCurrentNavigationItem] = useState<NavItemProps>()
  const [temporaryState, setTemporaryState] = useState<string>()
  const [productClassState, setProductClassState] = useState<any>()
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false)
  const [hasDirtyData, setHasDirtyData] = useState<boolean>(false)
  const [gettingClassState, setGettingClassState] = useState<boolean>(false)
  const [onCancelConfirmVisible, setOnCancelConfirmVisible] = useState<boolean>(false)
  const [onBackConfirmVisible, setOnBackConfirmVisible] = useState<boolean>(false)
  const [onNavigationConfirmVisible, setOnNavigationConfirmVisible] = useState<boolean>(false)
  const [editingNavigationItem, setEditingNavigationItem] = useState<string | undefined>()

  const { isLg } = useBreakPoints()
  const { data: wizard } = useQueryRequestV2<Wizard>({
    initialState: null,
    queryFn: () => props.getProductClassWizard(props.planPeriodId, productClassId),
  })

  let submitFormHandler: () => Promise<any>
  let goBackMethod: () => void
  let goNextMethod: () => void
  let getIndexOfActiveNavigationItemMethod: (items: WizardStep[]) => number = () => 0

  let redirectToMethod: (id: string) => void

  useEffect(() => {
    if (wizard) {
      setNavigationItems(wizard.steps)
      setInitialState(wizard.steps)
    }
  }, [wizard])
  useEffect(() => {
    if (currentNavigationItem) {
      setIsSubmiting(false)
      setHasDirtyData(false)
      setGettingClassState(true)

      props.getProductClassWizardState(props.planPeriodId, productClassId).then((res) => {
        setProductClassState(res)
        setProductClassStatus({
          editing: res.wizardStatus === WizardStatus.Completed,
          title: res.name,
        })
        setGettingClassState(false)
      })
    }
  }, [currentNavigationItem])

  const setInitialState = (navItems: WizardStep[]): void => {
    try {
      if (props.editingStep) {
        const getCurrentNavigationItem = navItems.find((item) => item.id === props.editingStep)
        if (!getCurrentNavigationItem) throw new Error('Navigation item not found')
        if (getCurrentNavigationItem.status !== WizardStepStatus.Disabled) {
          const getCurrentNavigationItemProps = props.navigationMapping.find(
            (navItem) => navItem.key === getCurrentNavigationItem.id,
          )
          setCurrentNavigationItem(getCurrentNavigationItemProps)
          setEditingNavigationItem(getCurrentNavigationItem.id)
        } else {
          loadActiveNavigationItem(navItems)
        }
      } else {
        loadActiveNavigationItem(navItems)
      }
    } catch (e) {
      console.log(e)
    }
  }

  const loadActiveNavigationItem = (items: WizardStep[]) => {
    const navigationItemIndex = getIndexOfActiveNavigationItemMethod(items)
    const getCurrentNavigationItem = items[navigationItemIndex]
    const getCurrentNavigationItemProps = props.navigationMapping.find(
      (navItem) => navItem.key === getCurrentNavigationItem.id,
    )
    setCurrentNavigationItem(getCurrentNavigationItemProps)
  }

  const getBackButton = (): ReactNode => {
    if (currentNavigationItem && navigationItems.length > 0) {
      if (navigationItems[0].id === currentNavigationItem.key) {
        return getButtonBasedOnType('cancel')
      } else {
        return getButtonBasedOnType('back')
      }
    }
    return null
  }

  const getNextButton = (): ReactNode => {
    if (currentNavigationItem && navigationItems.length > 0) {
      let button = null
      if (navigationItems[navigationItems.length - 1].id === currentNavigationItem.key) {
        button = getButtonBasedOnType('finish')
      } else if (productClassStatus && productClassStatus.editing) {
        button = getButtonBasedOnType('save')
      } else {
        button = getButtonBasedOnType('next')
      }
      if (currentNavigationItem.nextButtonTooltip && !isFormValid) {
        return (
          <Tooltip title={translate(currentNavigationItem.nextButtonTooltip)} position="left">
            {button}
          </Tooltip>
        )
      } else {
        return button
      }
    }
    return null
  }

  const getButtonBasedOnType = (type: BackButtonType | NextButtonType) => {
    switch (type) {
      case 'back':
        return (
          <Button
            size="default"
            icon="fa fa-arrow-left"
            className={'t-class-design-wizard-back'}
            htmlType="button"
            type="default"
            onClick={backHandler}
          >
            Previous
          </Button>
        )
      case 'cancel':
        return (
          <Button
            size="default"
            className={'t-class-design-wizard-cancel'}
            htmlType="button"
            type="default"
            onClick={cancleHandler}
          >
            Cancel
          </Button>
        )
      case 'next':
        return (
          <Button
            icon="fa fa-arrow-right"
            iconPosition="right"
            size="default"
            htmlType="submit"
            type="primary"
            disabled={!isFormValid}
            onClick={submitHandler}
            className="right t-class-design-wizard-next"
            loading={isSubmiting}
          >
            {isSubmiting ? 'Saving' : 'Next'}
          </Button>
        )
      case 'finish':
        return (
          <Button
            size="default"
            htmlType="submit"
            type="primary"
            disabled={!isFormValid}
            onClick={submitHandler}
            className="right t-class-design-wizard-finish"
            loading={isSubmiting}
          >
            {isSubmiting ? 'Saving' : 'Finish'}
          </Button>
        )
      case 'save':
        return (
          <Button
            size="default"
            htmlType="submit"
            type="primary"
            disabled={!isFormValid}
            onClick={submitHandler}
            className="right t-class-design-wizard-save"
            loading={isSubmiting}
          >
            {isSubmiting ? 'Saving' : 'Save'}
          </Button>
        )
    }
  }

  const backHandler = () => {
    if (hasDirtyData) {
      setOnBackConfirmVisible(true)
    } else {
      goBackMethod()
    }
  }

  const cancleHandler = () => {
    if (hasDirtyData) {
      setOnCancelConfirmVisible(true)
    } else {
      props.afterClose()
    }
  }

  const submitHandler = async (): Promise<any> => {
    setIsSubmiting(true)
    await submitFormHandler()
    props.onSubmit && props.onSubmit()
  }

  const goNext = () => {
    goNextMethod()
  }

  const finish = () => {
    setIsSubmiting(false)
    props.afterClose()
    Notification.open({
      message:
        props.finishWizardMessage ?? translate('admin-dashboard.benefit-details.class-design.added-successfully'),
      description: '',
      type: 'success',
    })
  }

  const save = () => {
    setIsSubmiting(false)
    props.afterClose()
    Notification.open({
      message: 'Changes saved successfully',
      type: 'success',
    })
  }

  return (
    props.planPeriodId && (
      <Modal
        visible={props.visible}
        width={896}
        closable={true}
        className="product_class"
        onCancel={cancleHandler}
        destroyOnClose={true}
        footer={null}
      >
        <>
          <Layout className="product_class__content">
            {isLg && (
              <Sider width={252}>
                <SidebarNavigation
                  navigationMapping={props.navigationMapping}
                  translate={translate}
                  navigationItems={navigationItems}
                  setCurrentNavigationItem={setCurrentNavigationItem}
                  setTemporaryState={setTemporaryState}
                  setOnNavigationConfirmVisible={setOnNavigationConfirmVisible}
                  hasDirtyData={hasDirtyData}
                  editingNavigationItem={editingNavigationItem}
                  setEditingNavigationItem={setEditingNavigationItem}
                  bindGoBack={(goBack: () => void) => {
                    goBackMethod = goBack
                  }}
                  bindGoNext={(goNext: () => void) => {
                    goNextMethod = goNext
                  }}
                  bindRedirectTo={(redirectTo: (id: string) => void) => {
                    redirectToMethod = redirectTo
                  }}
                  bindGetIndexOfActiveNavigationItem={(
                    getIndexOfActiveNavigationItem: (items: WizardStep[]) => number,
                  ) => {
                    getIndexOfActiveNavigationItemMethod = getIndexOfActiveNavigationItem
                  }}
                  wizardTitle={props.wizardTitle}
                />
              </Sider>
            )}
            <Content>
              <Header className="product_class__header">{getModalTitleContent(productClassStatus)}</Header>
              <Content className="product_class__body">
                <Skeleton
                  loading={!currentNavigationItem || !productClassState || gettingClassState}
                  active
                  paragraph={{
                    rows: 5,
                  }}
                >
                  <>
                    {currentNavigationItem && productClassState && !gettingClassState ? (
                      <currentNavigationItem.component
                        name={currentNavigationItem.key}
                        editing={productClassStatus ? productClassStatus.editing : undefined}
                        setIsFormValid={setIsFormValid}
                        setIsSubmiting={setIsSubmiting}
                        setHasDirtyData={setHasDirtyData}
                        setProductClassId={setProductClassId}
                        bindSubmitFormHandler={(submitForm: any) => {
                          submitFormHandler = submitForm
                        }}
                        state={currentNavigationItem.mapState(productClassState)}
                        planPeriodId={props.planPeriodId}
                        goNext={goNext}
                        finish={finish}
                        save={save}
                      />
                    ) : null}
                  </>
                </Skeleton>
              </Content>
              <Footer className="product_class__footer">
                {getBackButton()}
                {getNextButton()}
              </Footer>
            </Content>
          </Layout>
          {onCancelConfirmVisible ? (
            <CancelDisclaimerModal
              onCancel={() => {
                setOnCancelConfirmVisible(false)
              }}
              onCancelButton={() => {
                setOnCancelConfirmVisible(false)
                props.afterClose()
              }}
              onOk={() => {
                setOnCancelConfirmVisible(false)
                submitHandler().then(() => {
                  props.afterClose()
                })
              }}
              translate={translate}
            />
          ) : null}
          {onBackConfirmVisible
            ? OnNavigationDisclaimerModal({
                onCancel: () => {
                  setOnBackConfirmVisible(false)
                  setIsSubmiting(false)
                  setIsFormValid(false)
                  setHasDirtyData(false)
                  goBackMethod()
                },
                onOk: () => {
                  setOnBackConfirmVisible(false)
                },
                translate,
              })
            : null}

          {onNavigationConfirmVisible
            ? OnNavigationDisclaimerModal({
                onCancel: () => {
                  setOnNavigationConfirmVisible(false)
                  if (temporaryState) {
                    setIsSubmiting(false)
                    setIsFormValid(false)
                    setHasDirtyData(false)
                    redirectToMethod(temporaryState)
                  }
                },
                onOk: () => {
                  setOnNavigationConfirmVisible(false)
                },
                translate,
              })
            : null}
        </>
      </Modal>
    )
  )
}

const getModalTitleContent = (productClassStatus: ProductClassStatus | undefined) => {
  if (productClassStatus) {
    if (!productClassStatus.editing)
      if (productClassStatus.title !== null) {
        return <h4 className="title">{productClassStatus.title}</h4>
      } else {
        return (
          <>
            <h4 className="title">Class name:</h4>
            <h4 className="subtitle">select class</h4>
          </>
        )
      }
    else if (productClassStatus.editing) {
      return (
        <>
          <h4 className="title">Editing class:</h4>
          <h4 className="subtitle subtitle_filled">{productClassStatus.title}</h4>
        </>
      )
    }
  }
  return null
}

export default ProductClassWizardModal
